// === ENCUESTA ===

export interface Encuesta {
  id: number
  uuid: string
  titulo: string
  descripcion: string
  imagen: string
  activa: boolean
  asociado_laboratorio: boolean
  laboratorio_nombre: string | null
  total_respuestas: number
  total_visualizaciones: number
  total_inicios: number
  tasa_conversion: number
  created_at: string
  mostrar_resultados: boolean
  permitir_multiples_respuestas: boolean
}

export interface EncuestaDetail extends Encuesta {
  campo_principal_activo: boolean
  campo_principal_etiqueta: string
  campo_principal_placeholder: string
  campo_principal_requerido: boolean
  url_redireccion: string
  rangos_resultados: RangoResultado[] | null
  footer: string
  fecha_inicio: string | null
  fecha_fin: string | null
  grupo_laboratorio_id: number | null
  campos: Campo[]
}

export interface RangoResultado {
  min: number
  max: number
  titulo: string
  descripcion: string
}

export type TipoCampo = 'texto' | 'email' | 'numero' | 'telefono' | 'textarea'
  | 'select' | 'radio' | 'checkbox' | 'fecha' | 'escala'

export interface Campo {
  id: number
  tipo: TipoCampo
  etiqueta: string
  descripcion: string
  requerido: boolean
  orden: number
  placeholder: string
  min_valor: number | null
  max_valor: number | null
  max_caracteres: number | null
  escala_etiquetas: Record<string, string> | null
  opciones: OpcionCampo[]
}

export interface OpcionCampo {
  id: number
  valor: string
  orden: number
}

// === PAYLOADS ===

export interface EncuestaCreatePayload {
  titulo: string
  descripcion?: string
  imagen?: string
  campo_principal_activo?: boolean
  campo_principal_etiqueta?: string
  campo_principal_placeholder?: string
  campo_principal_requerido?: boolean
  asociado_laboratorio?: boolean
  laboratorio_id?: number | null
  grupo_laboratorio_id?: number | null
  activa?: boolean
  url_redireccion?: string
  permitir_multiples_respuestas?: boolean
  mostrar_resultados?: boolean
  rangos_resultados?: RangoResultado[] | null
  footer?: string
  fecha_inicio?: string | null
  fecha_fin?: string | null
}

export interface EncuestaUpdatePayload extends Partial<EncuestaCreatePayload> {}

export interface CampoCreatePayload {
  tipo: TipoCampo
  etiqueta: string
  descripcion?: string
  requerido?: boolean
  orden?: number
  placeholder?: string
  min_valor?: number | null
  max_valor?: number | null
  max_caracteres?: number | null
  escala_etiquetas?: Record<string, string> | null
  opciones?: string[]
}

export interface CampoUpdatePayload extends Partial<CampoCreatePayload> {}

// === PAGINACIÓN ===

export interface PaginatedEncuestas {
  count: number
  page: number
  page_size: number
  total_pages: number
  results: Encuesta[]
}

// === RESPUESTAS ===

export interface SuccessResponse {
  message: string
  success: boolean
}

export interface ErrorResponse {
  error: string
}

// === REPORTES ===

export interface ReporteEncuesta {
  id: number
  uuid: string
  titulo: string
  total_visualizaciones: number
  total_inicios: number
  total_respuestas: number
  total_abandonos: number
  tasa_conversion: number
  tasa_finalizacion: number
  tasa_abandono: number
}

export interface ReporteResumen {
  total_encuestas: number
  total_vistas: number
  total_inicios: number
  total_respuestas: number
  encuestas: ReporteEncuesta[]
}

export interface SesionReporte {
  id: number
  uuid: string
  ip_address: string
  dispositivo: string
  fecha_completado: string | null
  tiempo_segundos: number | null
  nombre_paciente: string
  puntaje_total: number | null
  resultado_titulo: string | null
  respuestas: Record<string, string>
}

export interface CampoInfo {
  id: number
  etiqueta: string
  tipo: string
}

export interface PaginatedSesiones {
  count: number
  page: number
  page_size: number
  total_pages: number
  encuesta_titulo: string
  encuesta_imagen: string
  encuesta_activa: boolean
  mostrar_resultados: boolean
  campos: CampoInfo[]
  total_visualizaciones: number
  total_inicios: number
  total_respuestas: number
  tasa_conversion: number
  tasa_finalizacion: number
  tasa_abandono: number
  url_publica: string
  results: SesionReporte[]
}

export interface EstadisticaDiaria {
  fecha: string
  vistas: number
  inicios: number
  terminados: number
  tasa_conversion: number
}

export interface EstadisticasEncuesta {
  encuesta_titulo: string
  total_vistas: number
  total_inicios: number
  total_terminados: number
  tasa_conversion: number
  tasa_abandono: number
  stats_diarias: EstadisticaDiaria[]
}

// === LABELS ===

// === PÚBLICO ===

export interface EncuestaPublica {
  uuid: string
  titulo: string
  descripcion: string
  imagen: string
  campo_principal_activo: boolean
  campo_principal_etiqueta: string
  campo_principal_placeholder: string
  campo_principal_requerido: boolean
  footer: string
  mostrar_resultados: boolean
  es_multi_step: boolean
  sesion_uuid: string
  campos: Campo[]
}

export interface SubmitPayload {
  sesion_uuid: string
  respuestas: Record<string, string>
  campo_principal_valor?: string
}

export interface SubmitResult {
  success: boolean
  redirect_url: string
}

export interface GraciasData {
  titulo: string
  descripcion: string
  imagen: string
  total_respuestas: number
  tasa_finalizacion: number
  footer: string
}

export interface ResultadoInfo {
  puntaje: number
  titulo: string
  descripcion: string
  min: number
  max: number
}

export interface RespuestaDetalle {
  campo_id: number
  etiqueta: string
  valor: string
  valor_display: string
}

export interface ResultadosData {
  encuesta_titulo: string
  encuesta_imagen: string
  encuesta_footer?: string
  total_campos?: number
  sesion_uuid: string
  puntaje_total: number | null
  resultado: ResultadoInfo | null
  nombre_paciente: string
  edad_paciente: number | null
  tratamiento_actual: string
  url_redireccion: string
  respuestas: RespuestaDetalle[]
  campos: CampoInfo[]
}

export interface DatosPacientePayload {
  sesion_uuid: string
  nombre: string
  edad: number | null
  tratamiento: string
}

// === LABELS ===

export const TIPO_CAMPO_LABELS: Record<TipoCampo, string> = {
  texto: 'Texto',
  email: 'Email',
  numero: 'Número',
  telefono: 'Teléfono',
  textarea: 'Área de texto',
  select: 'Select',
  radio: 'Radio',
  checkbox: 'Checkbox',
  fecha: 'Fecha',
  escala: 'Escala',
}
